import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, KeyRound, Building2 } from 'lucide-react';
import api from '../services/apiClient';
import useAuthStore from '../store/authStore';
import { Button, Field, Input, Select } from '../components/ui';
import { toast } from '../components/toast';

const EMPTY = { name: '', email: '', password: '', role: 'operator', companyIds: [] };

export default function Users() {
  const me = useAuthStore((s) => s.user);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const isAdmin = me?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get('/users'), api.get('/companies', { limit: 100 })]);
      setUsers(u.items || []);
      setCompanies(c.items || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  const save = async (form) => {
    try {
      if (form.id) {
        await api.put(`/users/${form.id}`, { name: form.name, role: form.role, active: form.active, companyIds: form.companyIds });
        if (form.password) await api.post(`/users/${form.id}/password`, { password: form.password });
        toast.success('Usuario atualizado');
      } else {
        await api.post('/users', { name: form.name, email: form.email, password: form.password, role: form.role, companyIds: form.companyIds });
        toast.success('Usuario criado');
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (u) => {
    if (!confirm(`Excluir o usuario ${u.name}?`)) return;
    try { await api.del(`/users/${u.id}`); toast.success('Usuario excluido'); await load(); }
    catch (err) { toast.error(err.message); }
  };

  if (!isAdmin) return <p className="muted">Acesso restrito a administradores.</p>;

  return (
    <div>
      <div className="page-actions">
        <Button onClick={() => setEditing({ ...EMPTY })}><Plus size={16} /> Novo usuário</Button>
      </div>

      {loading ? <p className="muted">Carregando…</p> : (
        <table className="tbl">
          <thead>
            <tr><th>Nome</th><th>Email</th><th>Perfil</th><th>Empresas</th><th></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <strong>{u.name}</strong>
                  {!u.active && <span className="badge gray" style={{ marginLeft: 6 }}>inativo</span>}
                </td>
                <td className="muted">{u.email}</td>
                <td>{u.role === 'admin' ? <span className="badge blue">admin</span> : <span className="badge gray">operador</span>}</td>
                <td>
                  {u.role === 'admin'
                    ? <span className="muted">todas</span>
                    : (u.companies.length
                      ? <span className="row wrap" style={{ gap: 4 }}>{u.companies.map((c) => <span key={c.id} className="badge gray">{c.name}</span>)}</span>
                      : <span className="badge yellow">nenhuma</span>)}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Button variant="ghost" className="sm" onClick={() => setEditing({ ...u, password: '', companyIds: u.companies.map((c) => c.id) })}><Pencil size={14} /></Button>
                  <Button variant="ghost" className="sm" onClick={() => remove(u)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="muted center">Nenhum usuario.</td></tr>}
          </tbody>
        </table>
      )}

      {editing && <UserForm initial={editing} companies={companies} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function UserForm({ initial, companies, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || '',
    email: initial.email || '',
    password: '',
    role: initial.role || 'operator',
    active: initial.active !== undefined ? initial.active : true,
    companyIds: initial.companyIds || []
  });
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(form.id);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleCompany = (id) => setForm((f) => ({
    ...f,
    companyIds: f.companyIds.includes(id) ? f.companyIds.filter((x) => x !== id) : [...f.companyIds, id]
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="card mt16" style={{ maxWidth: 560 }}>
      <h3 style={{ marginTop: 0 }}>{isEdit ? 'Editar usuario' : 'Novo usuario'}</h3>
      <form onSubmit={submit}>
        <div className="grid cols-2">
          <Field label="Nome"><Input value={form.name} onChange={set('name')} required autoFocus /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={set('email')} required disabled={isEdit} /></Field>
        </div>
        <div className="grid cols-2">
          <Field label="Perfil">
            <Select value={form.role} onChange={set('role')}>
              <option value="operator">Operador (empresas especificas)</option>
              <option value="admin">Admin (ve todas)</option>
            </Select>
          </Field>
          <Field label={isEdit ? 'Nova senha (opcional)' : 'Senha'} hint={isEdit ? 'Deixe vazio para manter' : 'Minimo 6 caracteres'}>
            <Input type="password" value={form.password} onChange={set('password')} required={!isEdit} minLength={6} />
          </Field>
        </div>

        {isEdit && (
          <Field label="Status">
            <Select value={form.active ? '1' : '0'} onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === '1' }))}>
              <option value="1">Ativo</option>
              <option value="0">Inativo</option>
            </Select>
          </Field>
        )}

        {form.role === 'operator' && (
          <Field label="Empresas com acesso">
            <div className="company-pick">
              {companies.length === 0 && <span className="muted">Nenhuma empresa cadastrada.</span>}
              {companies.map((c) => (
                <label key={c.id} className={`pick-item ${form.companyIds.includes(c.id) ? 'on' : ''}`}>
                  <input type="checkbox" checked={form.companyIds.includes(c.id)} onChange={() => toggleCompany(c.id)} />
                  <Building2 size={14} /> {c.name}
                </label>
              ))}
            </div>
            <span className="hint">{form.companyIds.length} empresa(s) selecionada(s)</span>
          </Field>
        )}
        {form.role === 'admin' && <p className="hint">Admins enxergam todas as empresas automaticamente.</p>}

        <div className="row mt16">
          <Button type="submit" loading={saving}>Salvar</Button>
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}
